import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  ArrowLeft, Sparkles, Search, RefreshCw, Check, X, Clock,
  AlertCircle, CheckCircle, Eye, Calendar, Building2,
  DollarSign, Users, Loader2, Zap, User, FileText, Palette
} from 'lucide-react';
import { toast } from 'sonner';

import { formatDateMonthDay } from '../../utils/timeFormat';
const statusConfig = {
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700', icon: Clock },
  in_review: { label: 'In Review', color: 'bg-blue-100 text-blue-700', icon: Eye },
  in_progress: { label: 'In Progress', color: 'bg-purple-100 text-purple-700', icon: Sparkles },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  declined: { label: 'Declined', color: 'bg-red-100 text-red-700', icon: X }
};

const priorityConfig = {
  standard: { label: 'Standard', color: 'bg-slate-100 text-slate-700' },
  rush: { label: 'Rush', color: 'bg-red-100 text-red-700' }
};

const AdminCustomRequests = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    in_progress: 0,
    completed: 0
  });
  const [filters, setFilters] = useState({
    status: 'all',
    search: ''
  });
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [updateDialog, setUpdateDialog] = useState(false);
  const [updateForm, setUpdateForm] = useState({
    status: '',
    admin_notes: '',
    quoted_price: '',
    assigned_admin_id: ''
  });
  const [processing, setProcessing] = useState(false);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status !== 'all') params.append('status', filters.status);

      const response = await api.get(`/admin/custom-ambassador/requests?${params.toString()}`);
      setRequests(response.data || []);

      // Calculate stats
      const allRequests = response.data || [];
      setStats({
        total: allRequests.length,
        pending: allRequests.filter(r => r.status === 'pending').length,
        in_progress: allRequests.filter(r => r.status === 'in_progress').length,
        completed: allRequests.filter(r => r.status === 'completed').length
      });
    } catch (error) {
      console.error('Failed to fetch requests:', error);
      toast.error('Failed to load custom ambassador requests');
    } finally {
      setLoading(false);
    }
  }, [filters.status]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const openDetailsDialog = (request) => {
    setSelectedRequest(request);
    setDetailsDialog(true);
  };

  const openUpdateDialog = (request) => {
    setSelectedRequest(request);
    setUpdateForm({
      status: request.status,
      admin_notes: request.admin_notes || '',
      quoted_price: request.quoted_price?.toString() || '',
      assigned_admin_id: request.assigned_admin_id || ''
    });
    setUpdateDialog(true);
  };

  const handleUpdateRequest = async () => {
    setProcessing(true);
    try {
      await api.patch(`/admin/custom-ambassador/${selectedRequest.id}/status`, {
        status: updateForm.status,
        admin_notes: updateForm.admin_notes || null,
        quoted_price: updateForm.quoted_price ? parseFloat(updateForm.quoted_price) : null
      });

      toast.success('Request updated successfully');
      setUpdateDialog(false);
      setSelectedRequest(null);
      fetchRequests();
    } catch (error) {
      console.error('Failed to update request:', error);
      toast.error(error.response?.data?.detail || 'Failed to update request');
    } finally {
      setProcessing(false);
    }
  };

  const filteredRequests = requests.filter(request => {
    if (filters.search) {
      const search = filters.search.toLowerCase();
      return (
        request.brand_name?.toLowerCase().includes(search) ||
        request.client_email?.toLowerCase().includes(search) ||
        request.ambassador_name?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-custom-requests">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/admin/home')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  <Palette className="w-6 h-6 text-emerald-500" />
                  Custom Ambassador Requests
                </h1>
                <p className="text-muted-foreground">Manage bespoke AI Ambassador creation requests</p>
              </div>
            </div>
            <Button onClick={fetchRequests} variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-white">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Requests</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-3 bg-amber-100 rounded-lg">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pending Review</p>
                <p className="text-2xl font-bold">{stats.pending}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-3 bg-purple-100 rounded-lg">
                <Sparkles className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">In Progress</p>
                <p className="text-2xl font-bold">{stats.in_progress}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Completed</p>
                <p className="text-2xl font-bold">{stats.completed}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="bg-white mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by brand, client, ambassador name..."
                    value={filters.search}
                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                    className="pl-10"
                  />
                </div>
              </div>
              <Select
                value={filters.status}
                onValueChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="in_review">In Review</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="declined">Declined</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Requests Table */}
        <Card className="bg-white">
          <CardHeader>
            <CardTitle>Custom Ambassador Requests</CardTitle>
            <CardDescription>Review and manage client requests for bespoke AI Ambassadors</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="custom-requests-table">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left p-3 text-sm font-medium text-muted-foreground">Brand / Client</th>
                    <th className="text-left p-3 text-sm font-medium text-muted-foreground">Ambassador Name</th>
                    <th className="text-left p-3 text-sm font-medium text-muted-foreground">Industry</th>
                    <th className="text-left p-3 text-sm font-medium text-muted-foreground">Priority</th>
                    <th className="text-left p-3 text-sm font-medium text-muted-foreground">Quote</th>
                    <th className="text-left p-3 text-sm font-medium text-muted-foreground">Status</th>
                    <th className="text-left p-3 text-sm font-medium text-muted-foreground">Submitted</th>
                    <th className="text-left p-3 text-sm font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRequests.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="p-8 text-center text-muted-foreground">
                        No custom ambassador requests found
                      </td>
                    </tr>
                  ) : (
                    filteredRequests.map((request) => {
                      const status = statusConfig[request.status] || statusConfig.pending;
                      const priority = request.is_rush ? priorityConfig.rush : priorityConfig.standard;
                      const StatusIcon = status.icon;

                      return (
                        <tr key={request.id} className="border-b hover:bg-slate-50">
                          <td className="p-3">
                            <p className="font-medium">{request.brand_name}</p>
                            <p className="text-xs text-muted-foreground">{request.client_email}</p>
                          </td>
                          <td className="p-3">
                            <p className="font-medium">{request.ambassador_name || 'TBD'}</p>
                          </td>
                          <td className="p-3">
                            <p className="text-sm">{request.industry || '-'}</p>
                          </td>
                          <td className="p-3">
                            <Badge className={priority.color}>
                              {request.is_rush && <Zap className="w-3 h-3 mr-1" />}
                              {priority.label}
                            </Badge>
                          </td>
                          <td className="p-3 font-medium">
                            {request.quoted_price ? `$${request.quoted_price.toLocaleString()}` : '-'}
                          </td>
                          <td className="p-3">
                            <Badge className={status.color}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {status.label}
                            </Badge>
                          </td>
                          <td className="p-3 text-sm text-muted-foreground">
                            {formatDateMonthDay(request.created_at)}
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openDetailsDialog(request)}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                className="bg-emerald-500 hover:bg-emerald-600"
                                onClick={() => openUpdateDialog(request)}
                              >
                                Update
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Details Dialog */}
      <Dialog open={detailsDialog} onOpenChange={setDetailsDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Custom Ambassador Request Details</DialogTitle>
            <DialogDescription>
              Request for {selectedRequest?.brand_name}
            </DialogDescription>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-6">
              {/* Brand & Contact Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Brand Name</p>
                  <p className="font-medium">{selectedRequest.brand_name}</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Client Email</p>
                  <p className="font-medium">{selectedRequest.client_email}</p>
                </div>
              </div>

              {/* Ambassador Details */}
              <div className="space-y-4">
                <h4 className="font-medium flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Ambassador Details
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Ambassador Name</p>
                    <p className="font-medium">{selectedRequest.ambassador_name || 'Not specified'}</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Industry</p>
                    <p className="font-medium">{selectedRequest.industry || 'Not specified'}</p>
                  </div>
                </div>
                {selectedRequest.personality_traits && (
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Personality Traits</p>
                    <p className="font-medium">{selectedRequest.personality_traits}</p>
                  </div>
                )}
                {selectedRequest.visual_style && (
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Visual Style</p>
                    <p className="font-medium">{selectedRequest.visual_style}</p>
                  </div>
                )}
              </div>

              {/* Use Case */}
              {selectedRequest.use_case && (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Use Case Description</p>
                  <p className="font-medium whitespace-pre-wrap">{selectedRequest.use_case}</p>
                </div>
              )}

              {/* Priority & Status */}
              <div className="flex items-center gap-4">
                <Badge className={selectedRequest.is_rush ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-700'}>
                  {selectedRequest.is_rush && <Zap className="w-3 h-3 mr-1" />}
                  {selectedRequest.is_rush ? 'Rush Priority' : 'Standard Priority'}
                </Badge>
                <Badge className={statusConfig[selectedRequest.status]?.color}>
                  {statusConfig[selectedRequest.status]?.label}
                </Badge>
                {selectedRequest.quoted_price && (
                  <Badge className="bg-green-100 text-green-700">
                    <DollarSign className="w-3 h-3 mr-1" />
                    Quoted: ${selectedRequest.quoted_price.toLocaleString()}
                  </Badge>
                )}
              </div>

              {/* Admin Notes */}
              {selectedRequest.admin_notes && (
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-sm font-medium text-amber-800">Admin Notes</p>
                  <p className="text-amber-900 whitespace-pre-wrap">{selectedRequest.admin_notes}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailsDialog(false)}>
              Close
            </Button>
            <Button onClick={() => {
              setDetailsDialog(false);
              openUpdateDialog(selectedRequest);
            }}>
              Update Status
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Dialog */}
      <Dialog open={updateDialog} onOpenChange={setUpdateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Update Request</DialogTitle>
            <DialogDescription>
              Update status and details for {selectedRequest?.brand_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Status</Label>
              <Select
                value={updateForm.status}
                onValueChange={(value) => setUpdateForm(prev => ({ ...prev, status: value }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="in_review">In Review</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="declined">Declined</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Quoted Price ($)</Label>
              <Input
                type="number"
                value={updateForm.quoted_price}
                onChange={(e) => setUpdateForm(prev => ({ ...prev, quoted_price: e.target.value }))}
                placeholder="Enter quoted price"
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">Custom tier starts at $2,500</p>
            </div>
            <div>
              <Label>Admin Notes</Label>
              <Textarea
                value={updateForm.admin_notes}
                onChange={(e) => setUpdateForm(prev => ({ ...prev, admin_notes: e.target.value }))}
                placeholder="Internal notes about this request..."
                rows={3}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setUpdateDialog(false)}
              disabled={processing}
            >
              Cancel
            </Button>
            <Button
              className="bg-emerald-500 hover:bg-emerald-600"
              onClick={handleUpdateRequest}
              disabled={processing}
            >
              {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCustomRequests;
